import { useEffect, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  signalRService,
  type TicketNotification,
  type StatusChangeNotification,
  type DashboardUpdateNotification,
  type UserNotification,
  type LowStockNotification,
} from '@/lib/signalr';

/**
 * Hook for managing SignalR connection lifecycle
 */
export function useSignalRConnection() {
  const { token, isAuthenticated } = useAuthStore();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      signalRService.stop();
      setIsConnected(false);
      return;
    }

    const connect = async () => {
      try {
        await signalRService.start(token);
        setIsConnected(true);
        setError(null);
      } catch (err) {
        setError(err as Error);
        setIsConnected(false);
      }
    };

    connect();

    return () => {
      signalRService.stop();
      setIsConnected(false);
    };
  }, [isAuthenticated, token]);

  return { isConnected, error };
}

/**
 * Hook for subscribing to ticket updates
 */
export function useTicketUpdates(ticketId: string | undefined) {
  const [lastUpdate, setLastUpdate] = useState<TicketNotification | null>(null);
  const [statusChange, setStatusChange] = useState<StatusChangeNotification | null>(null);

  useEffect(() => {
    if (!ticketId || !signalRService.isConnected()) return;

    // Subscribe to ticket
    signalRService.subscribeToTicket(ticketId);

    // Listen for updates
    const unsubUpdate = signalRService.on<TicketNotification>('TicketUpdated', (data) => {
      if (data.ticketId === ticketId) {
        setLastUpdate(data);
      }
    });

    const unsubStatus = signalRService.on<StatusChangeNotification>('TicketStatusChanged', (data) => {
      if (data.ticketId === ticketId) {
        setStatusChange(data);
      }
    });

    return () => {
      signalRService.unsubscribeFromTicket(ticketId);
      unsubUpdate();
      unsubStatus();
    };
  }, [ticketId]);

  return { lastUpdate, statusChange };
}

/**
 * Hook for subscribing to dashboard updates
 */
export function useDashboardUpdates() {
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<DashboardUpdateNotification | null>(null);

  useEffect(() => {
    if (!signalRService.isConnected()) return;

    signalRService.subscribeToDashboard();

    const unsubRefresh = signalRService.on<{ Type: string }>('DashboardRefresh', () => {
      setShouldRefresh(true);
    });

    const unsubUpdate = signalRService.on<DashboardUpdateNotification>('DashboardUpdate', (data) => {
      setLastUpdate(data);
      setShouldRefresh(true);
    });

    return () => {
      signalRService.unsubscribeFromDashboard();
      unsubRefresh();
      unsubUpdate();
    };
  }, []);

  const clearRefresh = useCallback(() => {
    setShouldRefresh(false);
  }, []);

  return { shouldRefresh, lastUpdate, clearRefresh };
}

/**
 * Hook for listening to all ticket events (for list pages)
 */
export function useTicketListUpdates() {
  const [newTicket, setNewTicket] = useState<TicketNotification | null>(null);
  const [updatedTicket, setUpdatedTicket] = useState<TicketNotification | null>(null);
  const [statusChange, setStatusChange] = useState<StatusChangeNotification | null>(null);

  useEffect(() => {
    if (!signalRService.isConnected()) return;

    const unsubCreated = signalRService.on<TicketNotification>('TicketCreated', (data) => {
      setNewTicket(data);
    });

    const unsubUpdated = signalRService.on<TicketNotification>('TicketUpdated', (data) => {
      setUpdatedTicket(data);
    });

    const unsubStatus = signalRService.on<StatusChangeNotification>('TicketStatusChanged', (data) => {
      setStatusChange(data);
    });

    return () => {
      unsubCreated();
      unsubUpdated();
      unsubStatus();
    };
  }, []);

  const clearNewTicket = useCallback(() => setNewTicket(null), []);
  const clearUpdatedTicket = useCallback(() => setUpdatedTicket(null), []);
  const clearStatusChange = useCallback(() => setStatusChange(null), []);

  return {
    newTicket,
    updatedTicket,
    statusChange,
    clearNewTicket,
    clearUpdatedTicket,
    clearStatusChange,
  };
}

/**
 * Hook for personal user notifications
 */
export function useUserNotifications() {
  const addNotification = useNotificationStore((state) => state.addNotification);

  useEffect(() => {
    if (!signalRService.isConnected()) return;

    const unsubUser = signalRService.on<UserNotification>('UserNotification', (data) => {
      addNotification({
        title: data.title,
        message: data.message,
        type: data.type,
        actionUrl: data.actionUrl,
      });
    });

    const unsubAssigned = signalRService.on<TicketNotification>('TicketAssignedToYou', (data) => {
      addNotification({
        title: 'טיקט חדש הוקצה לך',
        message: `טיקט #${data.ticketNumber} - ${data.customerName} (${data.deviceName})`,
        type: 'info',
        actionUrl: `/tickets/${data.ticketId}`,
      });
    });

    return () => {
      unsubUser();
      unsubAssigned();
    };
  }, [addNotification]);
}

/**
 * Hook for low stock alerts
 */
export function useLowStockAlerts() {
  const [alerts, setAlerts] = useState<LowStockNotification[]>([]);
  const addNotification = useNotificationStore((state) => state.addNotification);

  useEffect(() => {
    if (!signalRService.isConnected()) return;

    const unsub = signalRService.on<LowStockNotification>('LowStockAlert', (data) => {
      setAlerts((prev) => [...prev, data]);
      addNotification({
        title: 'התראת מלאי נמוך',
        message: `${data.partName} (${data.sku}) - נותרו ${data.currentStock} יחידות`,
        type: 'warning',
        actionUrl: `/inventory?sku=${data.sku}`,
      });
    });

    return () => {
      unsub();
    };
  }, [addNotification]);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  return { alerts, clearAlerts };
}
